import DayjsExtended from "@logic/DayjsExtended";

/** Rolling windows offered by ReportDateRangePicker. "custom" carries hand-picked dates, "allTime" sends no bounds. */
export type ReportDateRangePreset = "last30Days" | "last90Days" | "yearToDate" | "last12Months" | "allTime" | "custom";

export interface ReportDateRangeValue {
  preset: ReportDateRangePreset;
  /** Local (browser timezone) YYYY-MM-DD. Omitted for the "allTime" preset. */
  startDate?: string;
  /** Local (browser timezone) YYYY-MM-DD. Omitted for the "allTime" preset. */
  endDate?: string;
}

export const reportDateRangePresetOptions: { value: ReportDateRangePreset; label: string }[] = [
  { value: "last30Days", label: "Last 30 days" },
  { value: "last90Days", label: "Last 90 days" },
  { value: "yearToDate", label: "Year to date" },
  { value: "last12Months", label: "Last 12 months" },
  { value: "allTime", label: "All time" },
  { value: "custom", label: "Custom range" },
];

export const DATE_RANGE_FORMAT = "YYYY-MM-DD";

/** Local YYYY-MM-DD for a Date (the server treats these as UTC day bounds; same local-day caveat as v1). */
export function toReportDateString(date: Date): string {
  return DayjsExtended(date).format(DATE_RANGE_FORMAT);
}

/**
 * Turns a preset into the inclusive local day window it stands for.
 * "allTime" and "custom" carry no computed dates ("custom" keeps whatever the user picked).
 */
export function resolveReportDateRange(preset: ReportDateRangePreset): ReportDateRangeValue {
  if (preset === "allTime" || preset === "custom") return { preset };

  const today = DayjsExtended();
  const endDate = today.format(DATE_RANGE_FORMAT);

  // Windows are inclusive of both ends, so "last 30 days" reaches back 29 days from today.
  if (preset === "last30Days") return { preset, startDate: today.subtract(29, "day").format(DATE_RANGE_FORMAT), endDate };
  if (preset === "last90Days") return { preset, startDate: today.subtract(89, "day").format(DATE_RANGE_FORMAT), endDate };
  if (preset === "last12Months") return { preset, startDate: today.subtract(1, "year").add(1, "day").format(DATE_RANGE_FORMAT), endDate };

  return { preset: "yearToDate", startDate: today.startOf("year").format(DATE_RANGE_FORMAT), endDate };
}

/** Trigger text, e.g. "Jan 1 – Aug 24, 2026", "Dec 1, 2025 – Feb 3, 2026" or "All time". */
export function formatReportDateRange(value: ReportDateRangeValue): string {
  if (!value.startDate && !value.endDate) return "All time";

  const start = value.startDate ? DayjsExtended(value.startDate, DATE_RANGE_FORMAT) : null;
  const end = value.endDate ? DayjsExtended(value.endDate, DATE_RANGE_FORMAT) : null;

  if (start?.isValid() && end?.isValid()) {
    const startText = start.year() === end.year() ? start.format("MMM D") : start.format("MMM D, YYYY");
    return `${startText} – ${end.format("MMM D, YYYY")}`;
  }
  if (start?.isValid()) return `Since ${start.format("MMM D, YYYY")}`;
  if (end?.isValid()) return `Through ${end.format("MMM D, YYYY")}`;

  return "All time";
}
